// FlixDetail.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { format, fromUnixTime } from 'date-fns';

import {
  Swiper,
  SwiperSlide,
  SwiperAutoHeightStyle,
} from '../component/Swiper.jsx';

import { formatTimeStringBySec } from '../resource/formatTimeString.js';
import { ButtonId } from '../resource/trackingConstants.js';
import { drmRulesetTestStatus as drmRulesetTestStatusConstant } from '../resource/drmConstants.js';
import { ProfileListType } from '../resource/profileConstants.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { LoadMode } from '../resource/playerConfig.js';

import FlixDetailReaction from '../container/FlixDetailReaction.js';
import MessageMeta from '../container/MessageMeta.js';
import MessageThumbnail from '../container/MessageThumbnail.js';
import MessageCategory from '../container/MessageCategory.js';
import FlixDetailList from '../container/FlixDetailList.js';
import WithMessagePinData from '../container/WithMessagePinData.js';
import Redirect from '../container/Redirect.js';

import BackIcon from '../../img/ic_topbar_back.svg';
import MoreIcon from '../../img/ic_topbar_more.svg';
import ProIconSrc from '../../img/ic_pro_badge.svg';
import PinnedGreyIconSrc from '../../img/ic_pin_grey.svg';

import { SanitizedLink as Link } from '../component/SanitizedLink.jsx';
import withRouter from '../component/WithRouter.jsx';
import Tabs from '../component/Tabs.jsx';
import Tab from '../component/Tab.jsx';
import Loading, { LoadingType } from '../component/Loading.jsx';
import UserFeedItem from '../container/UserFeedItem.js';

import {
  textColor,
  color,
  flixPlayerZIndex,
  breakpoint,
  headerHeight,
  footerNavigationHeight,
} from '../style/variables.js';
import media from '../style/media.js';
import ResetButtonStyle from '../style/ResetButtonStyle.js';
import FlixBadge from '../container/FlixBadge.js';
import LinkSubstring from '../container/LinkSubstring.js';
import { MessageMoreFeature } from '../resource/messageConstants.js';
import getShareLink from '../resource/getShareLink.js';
import withFetchTranslation from '../resource/withFetchTranslation.js';
import { PlayerContext } from '../context/PlayerContext.js';
import errorRoute from '../resource/errorRoute.js';

const FlixTab = {
  // PRO: 'pro',
  VIDEOS: 'videos',
  INFO: 'info',
  // FEED: 'feed',
};
const TabList = [FlixTab.VIDEOS, FlixTab.INFO];

let FlixPlayer;

/**
 * @typedef {Object} Props
 * @property {string} messageId
 * @property {string} senderId
 * @property {string} senderName
 * @property {boolean} canViewMedia
 * @property {string} title
 * @property {string} captionText
 * @property {number} postedAtUnix
 * @property {Error} detailError
 * @property {() => void} goBack
 * @property {({ id: string, httpProxyHeaders: object }) => Promise} fetchDetail
 * @property {(id: string) => Promise} fetchRating
 * @property {({ userId: string, httpProxyHeaders: object }) => Promise} fetchUser
 * @property {(userId: string) => Promise} followUser
 * @property {(userId: string) => Promise} unfollowUser
 * @property {(userId: string) => Promise} unblockUser
 * @property {(key: string) => string} t
 *
 * @typedef {Object} State
 * @property {boolean} shouldShowTranslation
 *
 * @extends {React.PureComponent<Props, State>}
 */
class FlixDetail extends React.PureComponent {
  state = {
    currentTab: FlixTab.VIDEOS,
    shouldShowTranslation: false,
    shouldHideAllTitle: true,
    isFirstMetadataLoaded: false,
    isFirstThumbnailLoaded: false,
    isOverflowed: false,
    isClient: false,
    playerContext: {
      player: null,
      setPlayer: player => {
        this.setState({
          playerContext: {
            ...this.state.playerContext,
            player,
          },
        });
      },
    },
  };
  isInitToggleTitle = true;
  swiper = undefined;
  setPlayer = () => null;
  constructor(props) {
    super(props);
    // fetch data for SSR
    const {
      isFeedFirstPageFetched,
      staticContext,
      messageId,
      fetchDetail,
      fetchUser,
      fetchFeeds,
      senderId,
      senderName,
      castIds,
      firstCastUsername,
      detailError,
    } = props;
    if (staticContext.actions) {
      if (detailError) {
        staticContext.error = detailError;
      }
      if (messageId && !senderId) {
        staticContext.actions.push({
          function: fetchDetail,
          argumentObject: {
            id: messageId,
            httpProxyHeaders: staticContext.httpProxyHeaders,
          },
        });
      }
      if (messageId && !isFeedFirstPageFetched) {
        staticContext.actions.push({
          function: fetchFeeds,
          argumentObject: {
            type: `message-${messageId}`,
            page: 1,
            httpProxyHeaders: staticContext.httpProxyHeaders,
          },
        });
      }
      if (senderId && !senderName) {
        staticContext.actions.push({
          function: fetchUser,
          argumentObject: {
            userId: senderId,
            httpProxyHeaders: staticContext.httpProxyHeaders,
          },
        });
      }
      if (castIds.length && !firstCastUsername) {
        staticContext.actions.push(
          ...castIds.map(castId => ({
            function: fetchUser,
            argumentObject: {
              userId: castId,
              httpProxyHeaders: staticContext.httpProxyHeaders,
            },
          }))
        );
      }
    }
  }
  updateBlockAndFolowingState = () => {
    const { fetchUserIsBlocked, fetchUserIsFollowing, senderId, isFollowing } =
      this.props;
    fetchUserIsBlocked({ userId: senderId });
    if (isFollowing == null) {
      fetchUserIsFollowing({ userId: senderId });
    }
  };
  componentDidMount() {
    const { fetchMe } = this.props;
    fetchMe();
    this.fetchData();
    this.updateBlockAndFolowingState();
    this.nextTick = setTimeout(() => {
      this.setState({ isClient: true });
    });
  }
  componentDidUpdate(prevProps) {
    const { messageId, isAuthed, senderId } = this.props;
    if (isAuthed !== prevProps.isAuthed) {
      this.fetchData();
      this.updateBlockAndFolowingState();
    }
    if (messageId !== prevProps.messageId) {
      this.fetchData();
      window.scrollTo(0, 0);
    }
    if (senderId !== prevProps.senderId) {
      this.updateBlockAndFolowingState();
    }
  }
  componentWillUnmount() {
    const { clearTopVideoPopupNotifications } = this.props;
    clearTimeout(this.updateAutoHeightTimeout);
    clearTimeout(this.nextTick);
    clearTopVideoPopupNotifications();
  }
  toggleTitle = () => {
    const { isOverflowed } = this.state;
    if (this.isInitToggleTitle && !isOverflowed) return;

    this.isInitToggleTitle = false;
    return this.setState(({ shouldHideAllTitle }) => ({
      shouldHideAllTitle: !shouldHideAllTitle,
    }));
  };

  renderPinnedBadge = () => {
    const { messageId, t } = this.props;
    return (
      <WithMessagePinData
        messageId={messageId}
        profileListType={ProfileListType.FLIX}
      >
        {({ isPinned }) =>
          isPinned ? (
            <PinnedBadgeWrapper>
              <PinIcon src={PinnedGreyIconSrc} />
              <PinText>
                {t('pinned_post', {
                  ns: TranslationNamespace.POST,
                })}
              </PinText>
            </PinnedBadgeWrapper>
          ) : null
        }
      </WithMessagePinData>
    );
  };
  renderBadge = () => {
    const { badges, messageBadgeNotShowList } = this.props;
    const filteredBadges = badges.filter(
      value => !messageBadgeNotShowList.includes(value)
    );

    return filteredBadges?.length ? (
      <BadgeWrapper>
        {filteredBadges.map(value => (
          <FlixBadge key={value} badgeName={value} />
        ))}
      </BadgeWrapper>
    ) : null;
  };
  renderTabs = () => {
    const { t, castIds } = this.props;
    const { currentTab } = this.state;
    return (
      <TabWrapper>
        <Tabs
          onChange={(event, tab) => {
            this.setState({
              currentTab: tab,
            });

            this.swiper.slideTo(TabList.indexOf(tab));

            if (tab === FlixTab.INFO && castIds.length) {
              // swiper might get wrong height on changed moment, since cast list rendering
              this.updateAutoHeightTimeout = setTimeout(() => {
                this.swiper?.updateAutoHeight();
              }, 500);
            }
          }}
          value={currentTab}
          indicatorStyle={{ minWidth: '76px' }}
        >
          <Tab
            as="button"
            data-element_id={ButtonId.Detail.ButtonRelatedVideo}
            key={FlixTab.VIDEOS}
            value={FlixTab.VIDEOS}
            label={t('tab_related_video', {
              ns: TranslationNamespace.POST,
            })}
            labelStyle={{ textAlign: 'center', minWidth: '76px' }}
          />
          <Tab
            as="button"
            data-element_id={ButtonId.Detail.ButtonInfo}
            key={FlixTab.INFO}
            value={FlixTab.INFO}
            label={t('tab_info', {
              ns: TranslationNamespace.POST,
            })}
            labelStyle={{ textAlign: 'center', minWidth: '76px' }}
          />
        </Tabs>
      </TabWrapper>
    );
  };
  renderTabsContent = () => {
    return (
      <StyledSwiper
        autoHeight={true}
        slidesPerView={1}
        onSwiper={swiper => {
          this.swiper = swiper;
          window.swiper = swiper;
        }}
        onSlideChangeTransitionEnd={swiper => {
          this.setState({
            currentTab: TabList[swiper.realIndex],
          });
        }}
      >
        <SwiperSlide key={FlixTab.VIDEOS}>
          {this.renderFlixRelatedList()}
        </SwiperSlide>
        <SwiperSlide key={FlixTab.INFO}>
          {this.renderDetailInfomation()}
        </SwiperSlide>
      </StyledSwiper>
    );
  };
  renderTranslationButton = () => {
    const { t, hasTranslation, isLanguageMatched } = this.props;
    return (
      hasTranslation &&
      !isLanguageMatched && (
        <TranslationToggle
          role="button"
          onClick={() => {
            this.setState(prevState => ({
              shouldShowTranslation: !prevState.shouldShowTranslation,
            }));
          }}
        >
          {this.state.shouldShowTranslation
            ? t('show_original', {
                ns: TranslationNamespace.POST,
              })
            : t('show_translate', {
                ns: TranslationNamespace.POST,
              })}
        </TranslationToggle>
      )
    );
  };
  renderCastInfo = () => {
    const { t, castIds } = this.props;
    return (
      !!castIds.length && (
        <CastWrapper>
          <CastTitle>
            {t('cast', {
              ns: TranslationNamespace.POST,
            })}
          </CastTitle>
          <CastList>
            {castIds.map((id, index) => (
              <CastItem key={index}>
                <UserFeedItem id={id} size={64} withHalo marginTop={12} />
              </CastItem>
            ))}
          </CastList>
        </CastWrapper>
      )
    );
  };
  renderFlixRelatedList = () => {
    const { messageId, isPublished } = this.props;
    return (
      <FlixDetailList
        listType={isPublished ? 'relatedMessages' : 'outbox'}
        messageId={messageId}
        shouldTrackRecommendEvent
        onChange={() => {
          if (this.swiper) {
            // re-calculate height if message list changed
            this.updateAutoHeightTimeout = setTimeout(() => {
              this.swiper?.updateAutoHeight();
            }, 0);
          }
        }}
      />
    );
  };
  renderMainInfomation = () => {
    const { shouldShowTranslation, shouldHideAllTitle, isOverflowed } =
      this.state;
    const { isPro, title, originalTitle, messageId, detailError, isFree } =
      this.props;
    // hide the reaction row if detail was not found
    const isNotFound = detailError && detailError.message === '404';
    return (
      <MainInfo>
        {this.renderPinnedBadge()}
        {this.renderBadge()}
        <Title
          ref={node => {
            this.setState({
              isOverflowed: node?.clientHeight !== node?.scrollHeight,
            });
          }}
          isOverflowed={isOverflowed}
          shouldHideAllTitle={shouldHideAllTitle}
          onClick={this.toggleTitle}
        >
          {isPro && (
            <ProIcon src={ProIconSrc} width="16" height="16" alt="pro icon" />
          )}
          <LinkSubstring>
            {shouldShowTranslation ? title : originalTitle}
          </LinkSubstring>
        </Title>
        <TranslationTogglerWrapper>
          {this.renderTranslationButton()}
        </TranslationTogglerWrapper>
        <MetaWrapper>
          <MessageMeta id={messageId} showLikesText />
          <MetaPartition>・</MetaPartition>
          <MessageMeta
            id={messageId}
            showViewCountText={isFree}
            showUnlockCountText={!isFree}
          />
        </MetaWrapper>
        {!isNotFound && (
          <FlixDetailReactionWrapper>
            <FlixDetailReaction messageId={messageId} shouldShowUser />
          </FlixDetailReactionWrapper>
        )}
      </MainInfo>
    );
  };
  renderDetailInfomation = () => {
    const {
      t,
      captionText,
      originalCaptionText,
      messageId,
      postedAtUnix,
      scheduledAtUnix,
      duration,
      isPublished,
    } = this.props;
    const { shouldShowTranslation, isClient } = this.state;
    const displayCaption = shouldShowTranslation
      ? captionText
      : originalCaptionText;
    return (
      <div>
        {/* {this.renderCastInfo()} */}
        <MoreInfo>
          <TranslationTogglerWrapper>
            {this.renderTranslationButton()}
          </TranslationTogglerWrapper>
          {displayCaption && (
            <Description>
              <LinkSubstring>{displayCaption}</LinkSubstring>
            </Description>
          )}
          <MessageCategory
            messageId={messageId}
            styles={{ paddingBottom: '16px' }}
          />
          <Duration>
            {t('description_video_length', {
              ns: TranslationNamespace.POST,
            })}{' '}
            {duration ? formatTimeStringBySec(duration) : '-'}
          </Duration>
          {!isPublished ? (
            <ScheduledAt>
              {t('schedule_time', {
                ns: TranslationNamespace.POST,
                date:
                  /* render formated time on SSR will causing hydration fail due to timezone */
                  isClient && scheduledAtUnix
                    ? format(fromUnixTime(scheduledAtUnix), 'yyyy-MM-dd HH:mm')
                    : '',
              })}
            </ScheduledAt>
          ) : (
            <CreatedAt>
              {t('description_post_time', {
                ns: TranslationNamespace.POST,
              })}
              {/* render formated time on SSR will causing hydration fail due to timezone */}
              {isClient
                ? format(fromUnixTime(postedAtUnix), 'yyyy/MM/dd HH:mm')
                : '-'}
            </CreatedAt>
          )}
          <CopyrightWrapper>
            <Copyright>
              {t('hint_copyright', {
                ns: TranslationNamespace.POST,
              })}
            </Copyright>
            <Copyright>
              {t('broadcast_disclaimer_workoffiction', {
                ns: TranslationNamespace.POST,
              })}
            </Copyright>
            <Copyright>
              {t('hint_adult', {
                ns: TranslationNamespace.POST,
              })}
            </Copyright>
          </CopyrightWrapper>
        </MoreInfo>
      </div>
    );
  };
  loadedMetadataHandler = () => {
    this.setState({ isFirstMetadataLoaded: true });
  };
  thumbnailLoadedHandler = async () => {
    if (!this.state.isFirstThumbnailLoaded) {
      if (!FlixPlayer) {
        FlixPlayer = (await import('../container/FlixPlayer.js')).default;
      }
      this.setState({ isFirstThumbnailLoaded: true });
    }
  };
  render() {
    const {
      messageId,
      originalTitle,
      isAuthed,
      canViewMedia,
      isVideoControlDisplayed,
      isPublished,
      isPlayerFullscreen,
      assetIds,
      toggleModal,
      goBack,
      browserDrmRulesetTeststatus,
      thumbnailSize,
      fetchMessageDetailError,
    } = this.props;
    const { playerContext, isFirstThumbnailLoaded, isFirstMetadataLoaded } =
      this.state;

    if (fetchMessageDetailError?.message) {
      const { path, status } = errorRoute(fetchMessageDetailError.message);

      return <Redirect to={path} status={status} replace />;
    }

    return (
      <PlayerContext.Provider value={playerContext}>
        <StyledPage>
          <PlayerMainWrapper isPlayerFullscreen={isPlayerFullscreen}>
            <PlayerSize>
              <Player>
                {/*
                1. Try to make the LCP element load faster
                   by delaying rendering the player until the thumbnail loaded.
                2. Currently we only show the very first thumbnail to avoid
                   thumbnail flickering or displaying old thumbnail before loading next video.
                */}
                {FlixPlayer &&
                isFirstThumbnailLoaded &&
                browserDrmRulesetTeststatus !==
                  drmRulesetTestStatusConstant.TESTING ? (
                  <FlixPlayer
                    messageId={messageId}
                    assetId={assetIds[0]}
                    loadMode={LoadMode.ACTIVE}
                    shouldUseSplash={true}
                    alt={originalTitle}
                    // Keep controls for logged-in users (including locked-preview overlay)
                    // and enable them for guests on free videos
                    shouldShowControls={isAuthed || !!canViewMedia}
                    onLoadedMetadata={this.loadedMetadataHandler}
                  />
                ) : (
                  <Loading size={64} type={LoadingType.TEALBLUE} />
                )}
              </Player>
              {/*
                Only show thumbnail for first-time render (mainly for SSR).
                It won't display any other thumbnail after the first video's metadata loaded.
                Which means the users would only see the loading icon if they click the next video on detail list.
              */}
              <ThumbnailWrapper shouldHidePoster={isFirstMetadataLoaded}>
                <MessageThumbnail
                  id={messageId}
                  size={thumbnailSize}
                  onLoad={this.thumbnailLoadedHandler}
                  objectFit="contain"
                  fetchPriority="high"
                />
              </ThumbnailWrapper>
              {isVideoControlDisplayed && (
                <BackButton
                  data-element_id={ButtonId.All.ButtonBack}
                  onClick={goBack}
                >
                  <img src={BackIcon} alt="back" />
                </BackButton>
              )}
              {isVideoControlDisplayed && isPublished && (
                <MoreButton
                  data-element_id={ButtonId.All.ButtonMore}
                  onClick={() => {
                    return toggleModal({
                      modalId: 'MessageMoreModal',
                      payload: {
                        messageId,
                        playerContext,
                        pinProfileListType: ProfileListType.FLIX,
                        features: [
                          MessageMoreFeature.BLOCK,
                          MessageMoreFeature.FOLLOW,
                          MessageMoreFeature.PROFILE,
                          MessageMoreFeature.CLIP,
                          MessageMoreFeature.PIN,
                          MessageMoreFeature.EDIT,
                          MessageMoreFeature.HIDE,
                        ],
                        shareLink: getShareLink({
                          pathname: `/post/${messageId}`,
                        }),
                      },
                      isOpen: true,
                    });
                  }}
                >
                  <img src={MoreIcon} alt="more" />
                </MoreButton>
              )}
            </PlayerSize>
          </PlayerMainWrapper>
          <PlayerSize />

          <Container>
            <Info>
              {this.renderMainInfomation()}
              <HiddenOnMobile>{this.renderDetailInfomation()}</HiddenOnMobile>
            </Info>
            <TabContainer>
              {this.renderTabs()}
              {this.renderTabsContent()}
            </TabContainer>
            <HiddenOnMobile>
              <Aside>{this.renderFlixRelatedList()}</Aside>
            </HiddenOnMobile>
          </Container>
        </StyledPage>
      </PlayerContext.Provider>
    );
  }

  renderTag = (text, index) => {
    return (
      <HashTag key={index} to={encodeURI(`/hashtag/${text}/flix`)} title={text}>
        {text}
      </HashTag>
    );
  };

  fetchData() {
    const {
      messageId,
      canViewMedia,
      isAuthed,
      fetchDetail,
      fetchUser,
      fetchRating,
      fetchViewScope,
    } = this.props;

    fetchDetail({ id: messageId }).then(
      () => this.props.senderId && fetchUser({ userId: this.props.senderId })
    );

    if (isAuthed) {
      fetchRating(messageId);
      if (!canViewMedia) {
        fetchViewScope({ id: messageId });
      }
    }
  }
}

FlixDetail.propTypes = {
  staticContext: PropTypes.object,
  t: PropTypes.func.isRequired,
  messageId: PropTypes.string.isRequired,
  senderId: PropTypes.string,
  browserDrmRulesetTeststatus: PropTypes.string,
  senderName: PropTypes.string,
  firstCastUsername: PropTypes.string,
  canViewMedia: PropTypes.bool,
  hasTranslation: PropTypes.bool,
  isVideoControlDisplayed: PropTypes.bool,
  title: PropTypes.string,
  originalTitle: PropTypes.string,
  captionText: PropTypes.string,
  originalCaptionText: PropTypes.string,
  postedAtUnix: PropTypes.number,
  scheduledAtUnix: PropTypes.number,
  thumbnailSize: PropTypes.number,
  detailError: PropTypes.instanceOf(Error),
  isFollowing: PropTypes.bool,
  isAuthed: PropTypes.bool,
  isPro: PropTypes.bool,
  isPlayerFullscreen: PropTypes.bool,
  goBack: PropTypes.func.isRequired,
  fetchViewScope: PropTypes.func,
  fetchDetail: PropTypes.func.isRequired,
  fetchRating: PropTypes.func,
  fetchUser: PropTypes.func,
  fetchMe: PropTypes.func.isRequired,
  duration: PropTypes.number,
  isFeedFirstPageFetched: PropTypes.bool,
  fetchMessageDetailError: PropTypes.instanceOf(Error),
  fetchFeeds: PropTypes.func,
  fetchUserIsBlocked: PropTypes.func,
  fetchUserIsFollowing: PropTypes.func,
  messageBadgeNotShowList: PropTypes.array,
  badges: PropTypes.array,
  clearTopVideoPopupNotifications: PropTypes.func,
  castIds: PropTypes.array,
  assetIds: PropTypes.array,
  isPublished: PropTypes.bool,
  isLanguageMatched: PropTypes.bool,
  isFree: PropTypes.bool,
  toggleModal: PropTypes.func,
};

FlixDetail.defaultProps = {
  staticContext: {},
  title: 'no title',
  originalTitle: '',
  senderId: '',
  browserDrmRulesetTeststatus: '',
  senderName: '',
  firstCastUsername: '',
  captionText: '',
  originalCaptionText: '',
  postedAtUnix: 0,
  scheduledAtUnix: 0,
  thumbnailSize: 256,
  detailError: null,
  isFollowing: null,
  isAuthed: false,
  isPro: false,
  isFree: false,
  isPlayerFullscreen: false,
  hasTranslation: false,
  isVideoControlDisplayed: false,
  isFeedFirstPageFetched: false,
  isLanguageMatched: false,
  fetchMessageDetailError: null,
  fetchFeeds: () => null,
  fetchViewScope: () => null,
  fetchRating: () => null,
  fetchUser: () => null,
  previousRoute: '',
  duration: 0,
  fetchUserIsBlocked: () => null,
  fetchUserIsFollowing: () => null,
  messageBadgeNotShowList: [],
  badges: [],
  clearTopVideoPopupNotifications: () => null,
  castIds: [],
  assetIds: [],
  isPublished: true,
  toggleModal: () => null,
};

const StyledPage = styled.div`
  padding-top: calc(${headerHeight}px + var(--space-to-pinned-notification));
  min-height: 100vh;
  color: ${textColor.white};
`;

const Container = styled.div`
  padding-bottom: 100px;
  max-width: 1200px;
  background-color: ${color.neutral[10]};
  margin: 0 auto;

  display: flex;
  flex-direction: row;
  @media (max-width: ${breakpoint.mobile}px) {
    display: block;
  }
`;

const TabContainer = styled.div`
  display: none;
  @media (max-width: ${breakpoint.mobile}px) {
    display: block;
  }
`;

const HiddenOnMobile = styled.div`
  @media (max-width: ${breakpoint.mobile}px) {
    display: none;
  }
`;

const Aside = styled.div`
  position: relative;

  // With max-width, the layout would be messy if the title of a flix is too short.
  // E.g., /post/66ce8d08f22123ad51f8b71e
  width: 388px;
`;

const Info = styled.div`
  flex: 1;
  background-color: ${color.grey['900']};

  padding: 40px 20px 20px 20px;
  @media (max-width: ${breakpoint.mobile}px) {
    padding: 16px 16px 0 16px;
  }
`;

const MainInfo = styled.header``;

const PlayerMainWrapper = styled.main`
  position: absolute;
  left: 0;
  right: 0;
  z-index: ${({ isPlayerFullscreen }) => (isPlayerFullscreen ? 9 : 4)};
  width: 100%;
  background-color: ${color.black};
  video {
    width: inherit;
    height: inherit;
  }

  ${media.mobile`
    position: fixed;
  `};
`;

const Player = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const PlayerSize = styled.div`
  position: relative;
  padding-bottom: 60vh;
  width: 100%;

  ${media.mobile`
    padding-bottom: 56.25%;
    
    @media (orientation: landscape) {
      padding-bottom: calc(100vh - ${footerNavigationHeight}px - ${headerHeight}px);
    }
  `};
`;

const ThumbnailWrapper = styled.div.attrs(({ shouldHidePoster }) => ({
  style: {
    opacity: shouldHidePoster ? 0 : 1,
  },
}))`
  transition: opacity 0.2s;
  pointer-events: none;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
`;

const HeaderButton = styled.button`
  ${ResetButtonStyle};
  position: absolute;
  top: 12px;
  left: 16px;
  z-index: ${flixPlayerZIndex.backButton};
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 1;

  & > img {
    display: block;
    width: 28px;
    height: 28px;
    object-fit: contain;
  }

  @media (hover: hover) {
    :hover {
      background-color: ${color.neutral['30_alpha_20']};
      opacity: 1;
    }
  }

  ${media.mobile`
    display: none;
  `};
`;

const BackButton = styled(HeaderButton)``;

const MoreButton = styled(HeaderButton)`
  left: auto;
  right: 16px;
`;

const BadgeWrapper = styled.div`
  margin-bottom: 12px;
`;

const PinnedBadgeWrapper = styled.div`
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;

const PinIcon = styled.img`
  margin-right: 4px;
  width: 18px;
  height: 18px;
  object-fit: contain;
  ${media.mobile`
    width: 16px;
    height: 16px;
  `};
`;

const PinText = styled.div`
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
  color: ${color.neutral[55]};
  ${media.mobile`
    font-size: 12px;
  `};
`;

const Title = styled.h1`
  display: block;
  margin: 0px;
  margin-bottom: 8px;
  margin-top: -4px;
  width: 100%;
  font-size: 22px;
  @media (max-width: ${breakpoint.mobile}px) {
    margin-bottom: 8px;
    font-size: 16px;
  }
  font-weight: 600;
  line-height: 1.5;
  overflow: hidden;
  cursor: ${({ isOverflowed }) => (isOverflowed ? 'pointer' : 'auto')};
  ${({ shouldHideAllTitle }) =>
    shouldHideAllTitle
      ? css`
          display: -webkit-box;
          text-overflow: ellipsis;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 2;
        `
      : null}
`;

const ProIcon = styled.img`
  margin-right: 8px;
  display: inline-block;
  vertical-align: middle;
`;

const TranslationTogglerWrapper = styled.div`
  margin-bottom: 10px;
  :empty {
    margin-bottom: 0px;
  }
`;

const MetaWrapper = styled.div`
  display: flex;
  align-items: center;
  padding-top: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid ${color.neutral['30_alpha_15']};
  color: ${textColor.grey};
  font-size: 14px;
  @media (max-width: ${breakpoint.mobile}px) {
    border-bottom: none;
    font-size: 12px;
  }
`;

const MetaPartition = styled.div`
  width: 12px;
  height: 12px;
  margin: 0 4px;
  font-size: 14px;
  color: ${textColor.black};
`;

const TranslationToggle = styled.div`
  display: inline-block;
  cursor: pointer;
  font-size: 14px;
  @media (max-width: ${breakpoint.mobile}px) {
    /* margin-bottom: 10px; */
    font-size: 12px;
  }
  font-weight: 600;
  color: ${textColor.black};
`;

const HashTag = styled(Link)`
  margin-right: 4px;
  margin-bottom: 4px;
  border-radius: 10px;
  padding: 4px;
  background-color: ${color.neutral['100_alpha_20']};
  color: ${textColor.white};
  font-size: inherit;
  line-height: 1;

  &::before {
    content: '#';
  }
`;

const FlixDetailReactionWrapper = styled.div`
  width: 100%;
  padding: 16px 0;
  background-color: ${color.grey['900']};
  border-bottom: 1px solid ${color.neutral['30_alpha_15']};
  @media (max-width: ${breakpoint.mobile}px) {
    border-bottom: none;
  }
`;

const MoreInfo = styled.div`
  background-color: ${color.neutral[10]};
  padding: 20px 0;
  font-size: 14px;
  @media (max-width: ${breakpoint.mobile}px) {
    padding: 20px 16px;
    font-size: 12px;
  }
`;

const Description = styled.div`
  margin-bottom: 16px;
  color: ${textColor.grey};
  font-size: 16px;
  @media (max-width: ${breakpoint.mobile}px) {
    font-size: 14px;
  }
  line-height: 1.5;
  white-space: pre-wrap;
`;

const Duration = styled.div`
  margin-bottom: 16px;
  color: ${textColor.black};
  font-size: inherit;
  line-height: 1;
`;

const CreatedAt = styled.div`
  margin-bottom: 16px;
  color: ${textColor.black};
  font-size: inherit;
  line-height: 1;
`;

const ScheduledAt = styled(CreatedAt)``;

const CopyrightWrapper = styled.div`
  flex: auto;
  display: flex;
  flex-wrap: wrap;
`;

const Copyright = styled.div`
  margin-top: 8px;
  margin-right: 8px;
  padding: 2px 4px;
  max-width: 100%;
  flex: none;
  display: inline-flex;
  font-size: inherit;
  opacity: 0.5;
  color: ${color.white};
  background-color: ${color.neutral['30_alpha_50']};
`;

const TabWrapper = styled.div`
  background-color: ${color.neutral[10]};
  border-bottom: 1px solid ${color.neutral['30_alpha_15']};
`;

const CastWrapper = styled.div`
  padding: 20px 0;
  background-color: ${color.neutral[10]};
  border-bottom: 1px solid ${color.neutral['30_alpha_50']};
  @media (max-width: ${breakpoint.mobile}px) {
    padding: 20px 16px;
  }
`;

const CastTitle = styled.div`
  padding-bottom: 16px;
  font-size: 16px;
  font-weight: 600;
  color: ${color.white};
`;

const CastList = styled.div`
  display: flex;
  flex-direction: row;
  padding: 0 8px;

  overflow-y: hidden;
  overflow-x: scroll;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const CastItem = styled.div`
  position: relative;
  display: flex;
  margin-right: 20px;
  padding-top: 6px;
`;

const StyledSwiper = styled(Swiper)`
  ${SwiperAutoHeightStyle};
`;

export default withRouter(withFetchTranslation(FlixDetail));
